import React from "react";
import { createPortal } from "react-dom";

import type {
  ETFDashboardRow,
  EquityDashboardRow,
  EquityRatesSummary,
  InvestmentsMacroResponse,
  MacroCurveRegime,
  MoatConfidenceBand,
  ValuationZone,
} from "@features/investments/api";
import { GlossaryTerm } from "@features/investments/glossary/term";

export type ToggleFilter = "all" | "yes" | "no";
export type RowsPerPage = 10 | 25 | 50;
export type ResearchRateFilter = "all" | "beats3m" | "below3m" | "beats10y" | "below10y";
export type ResearchSort = "default" | "fcfSpread10yDesc" | "fcfSpread10yAsc" | "buySpread10yDesc" | "buySpread10yAsc";

export const PAGE_SIZE_OPTIONS: RowsPerPage[] = [10, 25, 50];

export function MetricCard({
  label,
  value,
  helpText,
}: {
  label: string;
  value: string;
  helpText?: string;
}): JSX.Element {
  return (
    <div className="investments-card workflow-card workflow-metric-card">
      <div className="workflow-metric-label">
        <span className="metric-label-with-info">
          <GlossaryTerm label={label} variant="metric" />
          {helpText && <MetricInfoTooltip label={label} message={helpText} />}
        </span>
      </div>
      <div className="workflow-metric-value">{value}</div>
    </div>
  );
}

export function MetricInfoTooltip({
  label,
  message,
  nativeTitleOnly = false,
}: {
  label: string;
  message: string;
  nativeTitleOnly?: boolean;
}): JSX.Element {
  const iconRef = React.useRef<HTMLButtonElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState<{
    left: number;
    top: number;
    width: number;
    placement: "top" | "bottom";
  } | null>(null);

  const updateTooltipPosition = React.useCallback(() => {
    if (typeof window === "undefined" || !iconRef.current) {
      return;
    }

    const iconRect = iconRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredTooltipWidth = 260;
    const tooltipWidth = Math.min(preferredTooltipWidth, window.innerWidth - viewportPadding * 2);
    const centeredLeft = iconRect.left + iconRect.width / 2;
    const minimumLeft = viewportPadding + tooltipWidth / 2;
    const maximumLeft = window.innerWidth - viewportPadding - tooltipWidth / 2;
    const clampedLeft = Math.min(Math.max(centeredLeft, minimumLeft), Math.max(minimumLeft, maximumLeft));
    const placeAbove = iconRect.top >= 72;

    setTooltipPosition({
      left: clampedLeft,
      top: placeAbove ? iconRect.top - 10 : iconRect.bottom + 10,
      width: tooltipWidth,
      placement: placeAbove ? "top" : "bottom",
    });
  }, []);

  React.useEffect(() => {
    if (!tooltipVisible) {
      return undefined;
    }

    updateTooltipPosition();

    function handleViewportChange(): void {
      updateTooltipPosition();
    }

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [tooltipVisible, updateTooltipPosition]);

  return (
    <span className={`metric-info${nativeTitleOnly ? " metric-info-native" : ""}`}>
      <button
        ref={iconRef}
        type="button"
        className="metric-info-icon"
        aria-label={`${label}: ${message}`}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
        onFocus={() => setTooltipVisible(true)}
        onBlur={() => setTooltipVisible(false)}
      >
        i
      </button>
      {tooltipVisible && tooltipPosition && typeof document !== "undefined" && createPortal(
        <span
          className="metric-info-tooltip metric-info-tooltip-portal"
          role="tooltip"
          data-placement={tooltipPosition.placement}
          style={{
            left: tooltipPosition.left,
            top: tooltipPosition.top,
            width: tooltipPosition.width,
            transform: tooltipPosition.placement === "top" ? "translate(-50%, -100%)" : "translate(-50%, 0)",
          }}
        >
          {message}
        </span>,
        document.body,
      )}
    </span>
  );
}

export function pill(value: string | null | undefined): JSX.Element {
  const normalized = (value || "N/A").toUpperCase().replace(/\s+/g, "-");
  return <span className={`action-pill action-${normalized.toLowerCase()}`}>{value || "N/A"}</span>;
}

export function filterResearchRows(
  rows: EquityDashboardRow[],
  filters: {
    familyFilter: string;
    actionFilter: ValuationZone | "";
    valuationFilter: ValuationZone | "";
    moatFilter: MoatConfidenceBand | "";
    warningsFilter: ToggleFilter;
    rateFilter: ResearchRateFilter;
    sort: ResearchSort;
    searchTerm: string;
  },
): EquityDashboardRow[] {
  const search = filters.searchTerm.trim().toLowerCase();
  return rows
    .filter((row) => {
      if (filters.familyFilter && !(row.families ?? []).includes(filters.familyFilter)) {
        return false;
      }
      if (filters.actionFilter && row.finalAction !== filters.actionFilter) {
        return false;
      }
      if (filters.valuationFilter && row.valuationZone !== filters.valuationFilter) {
        return false;
      }
      if (filters.moatFilter && row.moatBand !== filters.moatFilter) {
        return false;
      }
      if (filters.warningsFilter === "yes" && row.warningCount === 0) {
        return false;
      }
      if (filters.warningsFilter === "no" && row.warningCount > 0) {
        return false;
      }
      if (filters.rateFilter === "beats3m" && !isPositiveRateBaseline(row.ratesBaseline?.currentSpreadToThreeMonth)) {
        return false;
      }
      if (filters.rateFilter === "below3m" && !isNegativeOrZeroRateBaseline(row.ratesBaseline?.currentSpreadToThreeMonth)) {
        return false;
      }
      if (filters.rateFilter === "beats10y" && !isPositiveRateBaseline(row.ratesBaseline?.currentSpreadToTenYear)) {
        return false;
      }
      if (filters.rateFilter === "below10y" && !isNegativeOrZeroRateBaseline(row.ratesBaseline?.currentSpreadToTenYear)) {
        return false;
      }
      if (!search) {
        return true;
      }
      return row.ticker.toLowerCase().includes(search) || row.companyName.toLowerCase().includes(search);
    })
    .sort((left, right) => {
      if (filters.sort === "fcfSpread10yDesc") {
        return compareNullableNumbers(right.ratesBaseline?.currentSpreadToTenYear, left.ratesBaseline?.currentSpreadToTenYear);
      }
      if (filters.sort === "fcfSpread10yAsc") {
        return compareNullableNumbers(left.ratesBaseline?.currentSpreadToTenYear, right.ratesBaseline?.currentSpreadToTenYear);
      }
      if (filters.sort === "buySpread10yDesc") {
        return compareNullableNumbers(right.ratesBaseline?.buyThresholdSpreadToTenYear, left.ratesBaseline?.buyThresholdSpreadToTenYear);
      }
      if (filters.sort === "buySpread10yAsc") {
        return compareNullableNumbers(left.ratesBaseline?.buyThresholdSpreadToTenYear, right.ratesBaseline?.buyThresholdSpreadToTenYear);
      }
      const actionDelta = valuationRank(left.finalAction) - valuationRank(right.finalAction);
      if (actionDelta !== 0) {
        return actionDelta;
      }
      return right.overallScore - left.overallScore;
    });
}

export function filterEtfRows(rows: ETFDashboardRow[], searchTerm: string): ETFDashboardRow[] {
  const search = searchTerm.trim().toLowerCase();
  return rows.filter((row) => {
    if (!search) {
      return true;
    }
    return row.ticker.toLowerCase().includes(search) || row.companyName.toLowerCase().includes(search);
  });
}

export function paginate<T>(rows: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

export function buildEtfSummary(rows: ETFDashboardRow[]): {
  assetClasses: string[];
  medianExpenseRatio: number | null;
  largestAum: ETFDashboardRow | null;
} {
  const assetClasses = Array.from(new Set(rows.map((row) => row.etf?.assetClass || "").filter(Boolean))).sort();
  const expenseRatios = rows
    .map((row) => row.etf?.expenseRatio ?? null)
    .filter((value): value is number => typeof value === "number")
    .sort((left, right) => left - right);
  const medianExpenseRatio = expenseRatios.length === 0
    ? null
    : expenseRatios[Math.floor(expenseRatios.length / 2)];
  const largestAum = [...rows].sort((left, right) => (right.etf?.assetsUnderManagement ?? -1) - (left.etf?.assetsUnderManagement ?? -1))[0] ?? null;
  return {
    assetClasses,
    medianExpenseRatio,
    largestAum,
  };
}

export function formatCurrencyNullable(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "N/A";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPercentNullable(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${value.toFixed(2)}%`;
}

export function formatPercentPointNullable(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${value.toFixed(2)} pp`;
}

export function formatBasisPointsNullable(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${Math.round(value * 100)} bps`;
}

export function formatCountShare(count: number, total: number): string {
  if (!Number.isFinite(total) || total <= 0) {
    return `${count} / 0`;
  }
  return `${count} / ${total}`;
}

export function formatPercentShare(count: number, total: number): string {
  if (!Number.isFinite(total) || total <= 0) {
    return "N/A";
  }
  return `${((count / total) * 100).toFixed(0)}%`;
}

export function formatScoreValue(value: number): string {
  return `${value.toFixed(1)} / 100`;
}

export type DetailDateMeta = {
  fieldLabel: string;
  inlineLabel: string;
  value: string;
};

export function getDetailDateMeta(refreshedAt?: string | null, asOfDate?: string | null): DetailDateMeta {
  const refreshedDate = formatDateOnly(refreshedAt);
  if (refreshedDate) {
    return {
      fieldLabel: "Refreshed Date",
      inlineLabel: "refreshed",
      value: refreshedDate,
    };
  }

  return {
    fieldLabel: "As Of Date",
    inlineLabel: "as of",
    value: formatDateOnly(asOfDate) || "N/A",
  };
}

function formatDateOnly(value?: string | null): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || trimmed.startsWith("0001-01-01")) {
    return "";
  }
  const dateOnly = trimmed.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (dateOnly) {
    return dateOnly;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().slice(0, 10);
}

export function findMacroSeries(macro: InvestmentsMacroResponse | null | undefined, key: string) {
  return (macro?.seriesHistory || []).find((series) => series.key === key) ?? null;
}

export function describeCurveRegime(value: MacroCurveRegime | null | undefined): string {
  switch (value) {
    case "INVERTED":
      return "Inverted";
    case "FLAT":
      return "Flat";
    case "NORMAL":
      return "Normal";
    default:
      return "N/A";
  }
}

export function worstRatePressureFamilies(summary: EquityRatesSummary | null | undefined, limit: number): EquityRatesSummary["families"] {
  if (!summary) {
    return [];
  }
  return [...summary.families]
    .filter((family) => family.equityCount > 0)
    .sort((left, right) => {
      const medianDelta = compareNullableNumbers(left.medianCurrentSpreadToTenYear, right.medianCurrentSpreadToTenYear);
      if (medianDelta !== 0) {
        return medianDelta;
      }
      return left.name.localeCompare(right.name);
    })
    .slice(0, limit);
}

function valuationRank(value: string): number {
  switch (value) {
    case "BUY":
      return 0;
    case "HOLD":
      return 1;
    case "WATCHLIST":
      return 2;
    case "AVOID":
      return 3;
    default:
      return 4;
  }
}

function compareNullableNumbers(leftValue: number | null | undefined, rightValue: number | null | undefined): number {
  const leftDefined = typeof leftValue === "number" && Number.isFinite(leftValue);
  const rightDefined = typeof rightValue === "number" && Number.isFinite(rightValue);
  if (!leftDefined && !rightDefined) {
    return 0;
  }
  if (!leftDefined) {
    return 1;
  }
  if (!rightDefined) {
    return -1;
  }
  return leftValue - rightValue;
}

function isPositiveRateBaseline(value: number | null | undefined): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isNegativeOrZeroRateBaseline(value: number | null | undefined): boolean {
  return typeof value === "number" && Number.isFinite(value) && value <= 0;
}
